import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useI18nText } from '@/lib/utils/i18nText';

/**
 * Small help icon (?) next to the Tags column header.
 * Click → popover rendered via portal (escapes table overflow:hidden).
 */
export default function TagsHelpPopover() {
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePos = useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePos();
    const onScroll = () => updatePos();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, updatePos]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target as Node) &&
        (!popoverRef.current || !popoverRef.current.contains(e.target as Node))
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <span className="inline-flex items-center ml-1" style={{ verticalAlign: 'middle' }}>
      <button
        ref={btnRef}
        type="button"
        // onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        onMouseEnter={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        onMouseLeave={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-ggc-border-gray-dark text-white hover:bg-ggc-text-gray transition-colors"
        style={{ fontSize: '9px', fontWeight: 700, lineHeight: 1 }}
        aria-label={t('Tags help', '태그 도움말')}
      >
        ?
      </button>
      {open && createPortal(
        <div
          ref={popoverRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
          className="w-56 rounded-ggc-md border border-ggc-border-gray bg-white shadow-ggc-md p-2.5 ggc-text-caption-m text-ggc-text-primary whitespace-normal"
        >
          {t(
            'To view the full tag list, go to the ',
            '모든 태그 목록을 확인하시려면 ',
          )}
          <Link
            to="/ggc-tags"
            className="text-ggc-cta-blue hover:underline"
            onClick={() => setOpen(false)}
          >
            ggc-tag
          </Link>
          {t(' page.', ' 페이지로 이동하세요.')}
        </div>,
        document.body,
      )}
    </span>
  );
}
